import type { IAccount, INavigation, INavigationItem, IUser } from '@share/common-types';
import { Routes } from '@share/constants';

import { hasPersistedMemberSession } from './has-persisted-member-session';

const BOOKING_MENU_TITLE_KEYS = new Set(['hotels', 'hotel', 'condos', 'condo', 'vacation rentals']);

/** Booking tabs a signed-in public ClubSite member must always reach, even when absent from the menu API. */
const CONDO_MENU_TITLE_KEYS = ['condos', 'condo', 'vacation rentals'];

const PUBLIC_CLUBSITE_BOOKING_MENU_ITEMS: { item: INavigationItem; titleKeys: string[] }[] = [
  {
    item: { isPopup: false, title: 'Hotels', url: Routes.Search },
    titleKeys: ['hotels', 'hotel'],
  },
  {
    // Search page, not the member-benefits Condos landing (/condo).
    item: { isPopup: false, title: 'Condos', url: Routes.CondoSearch },
    titleKeys: CONDO_MENU_TITLE_KEYS,
  },
];

/** True when a menu URL is the Condos marketing landing (/condo), not /condo/deals or /condo/:id. */
function isCondoLandingMenuUrl(url: string | undefined): boolean {
  if (!url) {
    return false;
  }

  const path = url.split('?')[0].replace(/\/+$/, '');
  const condoIndex = path.lastIndexOf(Routes.Condo);
  if (condoIndex < 0) {
    return false;
  }

  return path.slice(condoIndex + Routes.Condo.length) === '';
}

function rewriteCondoMenuItemToSearch(item: INavigationItem): INavigationItem {
  const title = normalizeMenuTitle(item.title);
  let nextItem = item;

  if (CONDO_MENU_TITLE_KEYS.includes(title) && isCondoLandingMenuUrl(item.url)) {
    nextItem = { ...item, url: Routes.CondoSearch };
  }

  if (item.childItems?.length) {
    const childItems = item.childItems.map(rewriteCondoMenuItemToSearch);
    return { ...nextItem, childItems };
  }

  return nextItem;
}

/** Point Condos menu entries at /condo/deals for signed-in public ClubSite members. */
function rewriteCondoLandingUrlsForPublicClubSiteMember(
  mainItems: INavigation['main'] | undefined,
): INavigation['main'] | undefined {
  if (!mainItems?.length) {
    return mainItems;
  }

  return mainItems.map(rewriteCondoMenuItemToSearch);
}

export function isPublicClubSiteEnabled(account: IAccount | null | undefined): boolean {
  return (
    !!account?.enableRsiPublicCertificates &&
    typeof account.enableRsiPublicCertificatesOrganizationId === 'number' &&
    account.enableRsiPublicCertificatesOrganizationId > 0
  );
}

export function isPublicClubSiteAnonymousVisitor(
  account: IAccount | null | undefined,
): boolean {
  if (!isPublicClubSiteEnabled(account) || !account) {
    return false;
  }

  return !hasPersistedMemberSession(account);
}

/** Signed-in public ClubSite retail member (inline signup or login with persisted session). */
export function isPublicClubSiteMemberLoggedIn(
  account: IAccount | null | undefined,
): boolean {
  return isPublicClubSiteEnabled(account) && hasPersistedMemberSession(account);
}

/** Map public sign-up-public user payload onto the IUser shape used by login/session code. */
export function normalizePublicSignupUser(user: IUser): IUser {
  const isActiveState =
    typeof user.state === 'string' && user.state.toLowerCase() === 'active';
  const memberId = user.userAdditionalData?.memberId ?? user.keyid ?? 0;

  return {
    ...user,
    active: user.active ?? (isActiveState ? 1 : 0),
    userAdditionalData: {
      ...user.userAdditionalData,
      memberId,
    },
  };
}

/** Signed-in public ClubSite user holding certificates without a club memberId. */
export function isPublicClubSiteCertificateHolder(
  account: IAccount | null | undefined,
  user: IUser | null | undefined,
): boolean {
  if (!isPublicClubSiteEnabled(account) || !user || isPublicClubSiteAnonymousVisitor(account)) {
    return false;
  }

  // `keyid` is the club member id everywhere else in the app; `userAdditionalData.memberId`
  // is only set by normalizePublicSignupUser, so without the fallback a real member whose
  // payload omits it would be misread as a public certificate holder.
  const memberId = user.userAdditionalData?.memberId ?? user.keyid;
  return memberId == null || memberId === 0;
}

export interface ResolveCertificateClubSiteIdParams {
  account: IAccount | null | undefined;
  user: IUser | null | undefined;
  menuOrganizationId?: number | null;
}

/** Club site id for certificate API calls — member org when signed in, public org when anonymous or public holder. */
export function resolveCertificateClubSiteId({
  account,
  user,
  menuOrganizationId,
}: ResolveCertificateClubSiteIdParams): number | undefined {
  if (isPublicClubSiteCertificateHolder(account, user)) {
    return account?.enableRsiPublicCertificatesOrganizationId;
  }

  const memberOrgId = user?.userAdditionalData?.organizationId;
  if (memberOrgId) {
    return memberOrgId;
  }

  if (isPublicClubSiteEnabled(account)) {
    return account?.enableRsiPublicCertificatesOrganizationId;
  }

  if (menuOrganizationId) {
    return menuOrganizationId;
  }

  return undefined;
}

export function buildPublicClubSiteAnonymousNavigation(account: IAccount): INavigation {
  return {
    organizationId: account.enableRsiPublicCertificatesOrganizationId as number,
    main: [{ isPopup: false, title: 'Certificates', url: Routes.Certificates }],
    menu: [],
  } as INavigation;
}

function normalizeMenuTitle(title: string | undefined): string {
  return (title ?? '').trim().toLowerCase();
}

/** Hide Hotel/Condo tabs for anonymous public ClubSite visitors. */
export function filterNavigationMainForPublicClubSite(
  mainItems: INavigation['main'] | undefined,
  account: IAccount | null | undefined,
): INavigation['main'] {
  if (!mainItems?.length || !isPublicClubSiteAnonymousVisitor(account)) {
    return mainItems;
  }

  return mainItems
    .map((item) => {
      const title = normalizeMenuTitle(item.title);
      if (BOOKING_MENU_TITLE_KEYS.has(title)) {
        return null;
      }

      if (item.childItems?.length) {
        const childItems = item.childItems.filter(
          (child) => !BOOKING_MENU_TITLE_KEYS.has(normalizeMenuTitle(child.title)),
        );
        if (!childItems.length) {
          return null;
        }
        return { ...item, childItems };
      }

      return item;
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);
}

function hasMenuItemWithTitle(mainItems: INavigation['main'], titleKeys: string[]): boolean {
  return mainItems.some((item) => {
    if (titleKeys.includes(normalizeMenuTitle(item.title))) {
      return true;
    }

    return !!item.childItems?.some((child) => titleKeys.includes(normalizeMenuTitle(child.title)));
  });
}

/**
 * Add the Hotel/Condo tabs for a signed-in public ClubSite member, whose menu API response can
 * come back without them. Anonymous visitors and other accounts keep the menu they were given.
 * Existing Condos → /condo landing links are rewritten to /condo/deals.
 */
export function addBookingNavigationMainForPublicClubSiteMember(
  mainItems: INavigation['main'] | undefined,
  account: IAccount | null | undefined,
): INavigation['main'] {
  if (!isPublicClubSiteEnabled(account) || isPublicClubSiteAnonymousVisitor(account)) {
    return mainItems;
  }

  const items = rewriteCondoLandingUrlsForPublicClubSiteMember(mainItems) ?? [];
  const missingItems = PUBLIC_CLUBSITE_BOOKING_MENU_ITEMS.filter(
    ({ titleKeys }) => !hasMenuItemWithTitle(items, titleKeys),
  ).map(({ item }) => item);

  return missingItems.length ? [...missingItems, ...items] : items;
}

/** Public ClubSite main menu: booking tabs hidden when anonymous, guaranteed when signed in. */
export function resolveNavigationMainForPublicClubSite(
  mainItems: INavigation['main'] | undefined,
  account: IAccount | null | undefined,
): INavigation['main'] {
  return addBookingNavigationMainForPublicClubSiteMember(
    filterNavigationMainForPublicClubSite(mainItems, account),
    account,
  );
}

export function isPublicClubSiteBookingLandingPath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length < 2) {
    return true;
  }

  const section = segments[1]?.toLowerCase();
  return (
    section === 'home' ||
    section === 'search' ||
    section === 'condo' ||
    section === '' ||
    pathname.endsWith(`/${segments[0]}`) ||
    pathname.endsWith(`/${segments[0]}/`)
  );
}

export function isPublicClubSiteHotelSectionPath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  const section = segments[1]?.toLowerCase();
  return section === 'search' || section === 'hotel' || section === 'book';
}

export function isPublicClubSiteCondoSectionPath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  const section = segments[1]?.toLowerCase();
  return section === 'condo' || section === 'unit-book';
}
